import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Calendar, Clock } from "lucide-react";
import { getBlogPostsByAuthor } from "../lib/supabase";
import Pagination from "./Pagination";

export default function AuthorList() {
  const { author } = useParams();
  const [posts, setPosts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPosts, setTotalPosts] = useState(0);

  useEffect(() => {
    async function fetchPosts() {
      if (!author) return;

      try {
        setLoading(true);
        const { posts, total } = await getBlogPostsByAuthor(
          author,
          currentPage,
        );
        setPosts(posts);
        setTotalPosts(total);
      } catch (err) {
        console.error("Error fetching author posts:", err);
        setError("Failed to load posts");
      } finally {
        setLoading(false);
      }
    }

    fetchPosts();
  }, [author, currentPage]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#F98B3D]"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded-md">
        <p className="text-red-700">{error}</p>
      </div>
    );
  }

  if (!posts.length) {
    return (
      <div>
        <h1 className="blog-title">Author: {author}</h1>
        <div className="text-center py-12">
          <p className="text-gray-600">No posts found by this author.</p>
          <Link
            to="/blog"
            className="text-[#F98B3D] hover:text-[#e07a2c] mt-4 inline-block"
          >
            View all posts
          </Link>
        </div>
      </div>
    );
  }

  const authorInfo = posts[0]?.blog_authors;

  return (
    <div>
      {authorInfo && (
        <div className="mb-8">
          <div className="flex items-center mb-4">
            {authorInfo.image_url && (
              <img
                src={authorInfo.image_url}
                alt={authorInfo.name}
                className="w-16 h-16 rounded-full mr-4"
              />
            )}
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {authorInfo.name}
              </h1>
              {authorInfo.bio && (
                <p className="text-gray-600 mt-1">{authorInfo.bio}</p>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {posts.map((post) => (
          <article key={post.id} className="blog-card">
            {post.image_url && (
              <img
                className="w-full h-48 object-cover"
                src={post.image_url}
                alt={post.title}
              />
            )}
            <div className="p-6">
              <div className="blog-meta mb-2">
                <time
                  dateTime={post.published_at}
                  className="flex items-center"
                >
                  <Calendar className="h-4 w-4 mr-1" />
                  {new Date(post.published_at).toLocaleDateString("en-US", {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })}
                </time>
                <span>•</span>
                <span className="flex items-center">
                  <Clock className="h-4 w-4 mr-1" />
                  {post.read_time} min read
                </span>
              </div>
              {post.blog_categories && (
                <span className="category-tag mb-3">
                  {post.blog_categories.name}
                </span>
              )}
              <h2 className="text-xl font-semibold text-gray-900 mb-2">
                <Link
                  to={`/blog/${post.slug}`}
                  className="hover:text-[#F98B3D]"
                >
                  {post.title}
                </Link>
              </h2>
              <p className="text-gray-600">{post.excerpt}</p>
            </div>
          </article>
        ))}
      </div>
      <Pagination
        currentPage={currentPage}
        totalPages={Math.ceil(totalPosts / 10)}
        onPageChange={setCurrentPage}
      />
    </div>
  );
}
